import React from 'react';
import { Link } from 'react-router-dom';
import './foundation-olympiad.css';

const highlights = [
  {
    icon: 'target',
    title: 'Exam-Oriented Specially Designed Batches',
    description: 'Designed for Classes 8th to 12th students aiming for Olympiad excellence and advanced reasoning.',
  },
  {
    icon: 'model_training',
    title: 'Learning Outcomes',
    description: 'Strong analytical thinking, multi-step problem solving, and deep conceptual clarity across topics.',
  },
  {
    icon: 'web',
    title: 'Teaching Mode',
    description: 'Live Online + Classroom support with structured worksheets, tests, and guided doubt clearing.',
  },
];

const curriculum = [
  {
    title: 'Olympiad Science (Physics, Chemistry, Biology)',
    icon: 'science',
    body: 'Concept strengthening, advanced patterns, and olympiad-style multi-concept questions with regular practice sheets.',
  },
  {
    title: 'Olympiad Mathematics',
    icon: 'calculate',
    body: 'High-level reasoning, non-routine problems, and strategy-driven practice with timed sets and Plan wise  tests.',
  },
  {
    title: 'Mental Ability / Logical Reasoning',
    icon: 'psychology',
    body: 'Pattern recognition, reasoning frameworks, and speed-building practice with guided solutions.',
  },
];

const advantages = [
  {
    icon: 'emoji_events',
    title: 'Exam Strategy',
    description: 'Smart solving methods, time management, elimination techniques, and accuracy improvement sessions.',
  },
  {
    icon: 'insights',
    title: 'Performance Tracking',
    description: 'Topic-wise tracking, plan wise , analysis reports, and improvement plans to boost scores steadily.',
  },
  {
    icon: 'workspace_premium',
    title: 'Advanced Practice',
    description: 'Curated difficulty ladder: basics → advanced → olympiad challenge sets with discussion-based learning.',
  },
  {
    icon: 'diversity_1',
    title: 'Personal Mentorship',
    description: 'Small batches, personalized guidance, and structured follow-ups to keep students consistent.',
  },
];

const OlympiadPage: React.FC = () => {
  const guidanceFormUrl = "https://forms.gle/xdExtHf2Yss4ZaHq9";
  const brochureUrl = "https://drive.google.com/file/d/1l5rloH-eS_x0pKcriMMPvX-Mg5DIixDU/view?pli=1";
  return (
    <div className="fo-page">
      <div className="fo-container">
        <Link className="fo-back-link" to="/courses" aria-label="Back to Courses">
          <span className="material-symbols-outlined">arrow_back</span>
          <span>Back to Courses</span>
        </Link>

        {/* Tabs */}
        <div className="fo-tabs">
          <Link className="fo-tab" to="/courses/foundation-olympiad">
            Foundation (8th-10th)
          </Link>
          <Link className="fo-tab is-active" to="/courses/olympiad">
            Olympiad (8th-12th)
          </Link>
        </div>

        {/* Hero */}
        <section className="fo-hero fo-hero--olympiad">
          <div className="fo-hero__overlay" />
          <div className="fo-hero__content">
            <span className="fo-badge">
              <span className="material-symbols-outlined">emoji_objects</span>
              OLYMPIAD WING
            </span>

            <h1 className="fo-hero__title">
              Olympiad Courses:
              <br />
              Advanced Thinking
              <br />
              & Problem Solving
            </h1>

            <p className="fo-hero__subtitle">
              Strengthening reasoning, concept depth, and olympiad-style question practice to build confidence and rank-ready performance.
            </p>
            <p className="fo-safalta-text">
              📘 Foundations – Safalta (सफलता)
              <br />
              Where young minds are shaped early — for lifelong success 🌟
            </p>

            <div className="fo-hero__actions">
              <a className="fo-btn fo-btn--primary" href={guidanceFormUrl} target="_blank" rel="noopener noreferrer">
                Register for Career Guidance
              </a>
              <Link className="fo-btn fo-btn--secondary" to="/our-students">
                View Success Stories
              </Link>
            </div>
          </div>
        </section>

        {/* Highlights */}
        <section className="fo-section">
          <h2 className="fo-section__title">Course Highlights</h2>

          <div className="fo-grid fo-grid--3">
            {highlights.map((item) => (
              <article className="fo-card" key={item.title}>
                <div className="fo-card__icon">
                  <span className="material-symbols-outlined">{item.icon}</span>
                </div>
                <h3 className="fo-card__title">{item.title}</h3>
                <p className="fo-card__text">{item.description}</p>
              </article>
            ))}
          </div>
        </section>

        {/* Curriculum */}
        <section className="fo-section fo-section--spaced">
          <div className="fo-section__head">
            <div>
              <h2 className="fo-section__title">Curriculum</h2>
              <p className="fo-section__subtitle">
                Holistic Olympiad coverage across Science, Maths, and Reasoning with plan wise tests.
              </p>
            </div>
            <a className="fo-link" href={brochureUrl} target="_blank" rel="noopener noreferrer">
              Download Brochure
            </a>
          </div>

          <div className="fo-accordion">
            {curriculum.map((c) => (
              <details className="fo-accordion__item" key={c.title}>
                <summary className="fo-accordion__summary">
                  <span className="fo-accordion__left">
                    <span className="material-symbols-outlined">{c.icon}</span>
                    {c.title}
                  </span>
                  <span className="material-symbols-outlined fo-accordion__chev">expand_more</span>
                </summary>

                <div className="fo-accordion__body">
                  <p>{c.body}</p>
                </div>
              </details>
            ))}
          </div>

          <div className="fo-note">
            <span className="material-symbols-outlined">workspace_premium</span>
            <div>
              <strong>Program Includes:</strong> Plan wise  Olympiad tests + detailed solutions + mentorship + performance improvement plan.
            </div>
          </div>
        </section>

        {/* Advantage */}
        <section className="fo-section fo-section--spaced">
          <h2 className="fo-section__title">The S-CUBUS Advantage</h2>

          <div className="fo-grid fo-grid--2">
            {advantages.map((a) => (
              <article className="fo-adv" key={a.title}>
                <div className="fo-adv__icon">
                  <span className="material-symbols-outlined">{a.icon}</span>
                </div>
                <div>
                  <h3 className="fo-adv__title">{a.title}</h3>
                  <p className="fo-adv__text">{a.description}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="fo-cta">
          <div className="fo-cta__content">
            <h2 className="fo-cta__title">Start Early, Stay Ahead!</h2>
            <p className="fo-cta__text">
              Build Olympiad thinking today and unlock stronger performance in school + competitive exams.
            </p>
          </div>

          <div className="fo-cta__actions">
            <a className="fo-btn fo-btn--primary" href={guidanceFormUrl} target="_blank" rel="noopener noreferrer">
              Register for Career Guidance
            </a>
            <a className="fo-btn fo-btn--ghost" href={brochureUrl} target="_blank" rel="noopener noreferrer">
              <span className="material-symbols-outlined">download</span>
              Program Brochure
            </a>
          </div>
        </section>
      </div>
    </div>
  );
};

export default OlympiadPage;
